import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { OptimizedImage } from "@/components/OptimizedImage";
import heroImage from "@/assets/hero-professional-work-min.webp";

export const HomeHero = () => {
  return (
    <section className="pt-12 pb-12 md:pt-16 md:pb-16 lg:pt-20 lg:pb-20" style={{
      backgroundColor: 'hsl(40 30% 97%)'
    }}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-10 items-center max-w-6xl mx-auto">
          
          {/* Mobile Image - Shows on top for mobile */}
          <div className="md:hidden w-full max-w-md mx-auto">
            <div className="relative">
              <OptimizedImage
                src={heroImage}
                alt="Small business owners meeting with their CPA in Manassas, VA about bookkeeping, tax, and advisory support."
                className="w-full h-auto rounded-2xl object-cover aspect-video shadow-lg"
                priority
                width={448}
                height={252}
                sizes="(max-width: 768px) 90vw, 448px"
              />
            </div>
          </div>

          {/* Text Content - Left Side */}
          <div className="text-center md:text-left">
            {/* Headline */}
            {/* Eyebrow */}
            <p className="text-sm md:text-base lg:text-lg uppercase tracking-wider font-extrabold mb-3" style={{ color: 'hsl(350 70% 45%)' }}>
              Serving Small Businesses in Manassas, Northern Virginia & DC
            </p>

            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mb-4 md:mb-5 leading-tight text-black">More Than Accountants. Your Financial Partners for <span style={{
              color: 'hsl(350 70% 45%)'
            }}>Growth.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-base lg:text-lg mb-6 leading-relaxed text-black/70">Get <span className="font-semibold">proactive accounting, year-round tax strategy, and fractional CFO support</span> to boost profitability, avoid cash flow and tax surprises, and make smarter decisions with confidence.</p>
            
            {/* Primary CTA */}
            <div className="flex justify-center md:justify-start mb-3">
              <Link to="/consultation">
                <Button size="lg" className="text-base px-8 py-5 h-auto text-white border-0" style={{
                  backgroundColor: 'hsl(350 70% 45%)'
                }}>
                  Book Free Discovery Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* Microcopy */}
            <p className="text-xs mb-4 text-black/50">
              No obligation • Hear back within 24–48 hours
            </p>
            
            {/* Proof Strip */}
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4 text-sm text-black/60">
              {/* Reviews with Google icon */}
              <div className="flex items-center gap-2">
                <a href="https://www.bizpayo.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                  <div className="flex items-center justify-center w-5 h-5 rounded bg-blue-600 text-white text-[11px] font-bold">B</div>
                </a>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />)}
                </div>
                <span className="font-medium">4.9</span>
              </div>
              
              <span className="hidden sm:inline text-black/30">|</span>
              <span>15+ Years Experience</span>
              <span className="hidden sm:inline text-black/30">|</span>
              <span>1500+ Happy Clients</span>
            </div>
            
            {/* Location Line */}
            <p className="text-xs text-black/50 mt-3 text-center md:text-left">
              Serving NoVA/DC: Manassas • Gainesville • Haymarket • Fairfax • Sterling • McLean • Reston • Vienna • Falls Church • Leesburg • Herndon • Ashburn • Great Falls • Centreville • Arlington • Alexandria • Winchester • Fredericksburg • Washington, DC
            </p>
          </div>

          {/* Desktop Image - Right Side */}
          <div className="hidden md:flex justify-end">
            <div className="relative w-full">
              <OptimizedImage
                src={heroImage}
                alt="Small business owners meeting with their CPA in Manassas, VA about bookkeeping, tax, and advisory support."
                className="w-full h-auto rounded-3xl object-cover aspect-[4/3] shadow-xl"
                priority
                width={560}
                height={420}
                sizes="(max-width: 1024px) 45vw, 560px"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
